import React, { useState } from 'react';
import { X, User, Shield, Activity, Monitor, AlertCircle, Copy, CheckCircle } from 'lucide-react';

const ActivityDetailModal = ({ activity, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  if (!activity) return null;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const detailSections = [
    {
      title: 'User Information',
      icon: User,
      color: 'text-blue-600',
      items: [
        { label: 'Full Name', value: activity?.user_profiles?.full_name || 'N/A' },
        { label: 'Email', value: activity?.user_profiles?.email || 'N/A' },
        { label: 'Role', value: activity?.user_profiles?.role || 'N/A' },
        { label: 'Status', value: activity?.user_profiles?.status || 'N/A' }
      ]
    },
    {
      title: 'Activity Details',
      icon: Activity,
      color: 'text-purple-600',
      items: [
        { label: 'Action Type', value: activity?.activity_type?.replace(/_/g, ' ')?.toUpperCase() || 'N/A' },
        { label: 'Description', value: activity?.description || 'No description available' },
        { label: 'Timestamp', value: formatDateTime(activity?.created_at) },
        { label: 'Activity ID', value: activity?.id }
      ]
    },
    {
      title: 'Technical Information',
      icon: Monitor,
      color: 'text-green-600',
      items: [
        { label: 'IP Address', value: activity?.ip_address || 'N/A' },
        { label: 'User Agent', value: activity?.user_agent || 'N/A' }
      ]
    }
  ];

  // Display metadata if available
  const metadata = activity?.metadata || {};
  const hasMetadata = Object.keys(metadata)?.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Activity className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Activity Details</h2>
                <p className="text-indigo-100 text-sm mt-1">
                  Comprehensive activity information and context
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Detail Sections */}
          <div className="space-y-6">
            {detailSections?.map((section, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <section.icon className={`w-5 h-5 ${section?.color}`} />
                  <h3 className="text-lg font-semibold text-gray-900">{section?.title}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section?.items?.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <label className="text-sm font-medium text-gray-600 block mb-1">
                        {item?.label}
                      </label>
                      <div className="text-sm text-gray-900 bg-white p-3 rounded border border-gray-200 break-words">
                        {item?.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Metadata Section */}
            {hasMetadata && (
              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Additional Metadata</h3>
                </div>
                
                <div className="bg-white p-4 rounded border border-gray-200">
                  <pre className="text-sm text-gray-900 overflow-x-auto">
                    {JSON.stringify(metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Copy Activity ID */}
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Shield className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Activity Reference ID</p>
                    <p className="text-xs text-gray-600 mt-1 font-mono">{activity?.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(activity?.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy ID
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetailModal;